import React, { FC, useRef } from 'react';
import Grabber from '../grabber';
import type { KonvaEventObject } from 'konva/lib/Node';
import { useDragGesture } from 'src/hooks/useDragGesture';

type Point = { x: number; y: number };

interface VerticalHandleProps {
    /** Background-image pixel-space position (unscaled) — `x` moves on drag, `y` just follows
     * wherever the measure line currently is. */
    x: number;
    y: number;
    scale: number;
    offset: Point;
    cursorChange: (cursor: string) => void;
    onDragMove: (x: number) => void;
    onDragEnd: () => void;
}

/**
 * One of the two draggable left/right-edge markers, constrained to horizontal movement only.
 * Mirrors the mouse-event-based dragging used in `moveGrabbers.tsx` so re-renders mid-drag don't lose the gesture.
 */
const VerticalHandle: FC<VerticalHandleProps> = ({ x, y, scale, offset, cursorChange, onDragMove, onDragEnd }) => {
    // Pure drag bookkeeping -- never read in render -- refreshed every render so `handleDragMove`
    // (registered for the whole gesture by `useDragGesture`) always sees the latest `scale`.
    const dragRef = useRef({ startX: x, clientX: 0, scale });
    dragRef.current.scale = scale;

    const handleDragMove = (e: MouseEvent) => {
        const { startX, clientX, scale: currentScale } = dragRef.current;
        const deltaX = (clientX - e.clientX) / currentScale;
        onDragMove(startX - deltaX);
    };

    const { start } = useDragGesture({ onDragMove: handleDragMove, onDragEnd });

    const onDragStart = (e: KonvaEventObject<MouseEvent>) => {
        dragRef.current.startX = x;
        dragRef.current.clientX = e.evt.clientX;
        start();
    };

    const position = { x: x * scale + offset.x, y: y * scale + offset.y };
    return <Grabber position={position} cursorChange={cursorChange} onDragStart={onDragStart} />;
};

export default VerticalHandle;
